/* eslint-disable jsx-a11y/no-autofocus */
// autoFocus - допустимое prop для CodeInput
import type { FC } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import type { IRefObject } from '@alfa-bank/site-code-input/src/models';
import { secondsToTime } from '@alfa-bank/site-helpers-transform/time/seconds-to-time';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';

import { CODE_REQUIRED_LENGTH } from '../../layouts/sms-layout/hooks/use-validate-code';
import { useFormContext } from '../form/context';
import { Captcha } from '../captcha';

import type { TCodeInputField } from './models';

type TState = 'idle' | 'counting';

export const CodeInputField: FC<TCodeInputField> = ({
    Button,
    CodeInput,
    CountdownTimer,
    Spinner,
    Text,
    countdownTime,
    error,
    isPending,
    name = '',
    onComplete,
    onResetError,
    onRetry,
    styles,
}) => {
    const [state, setState] = useState<TState>('counting');
    const codeInputRef = useRef<IRefObject | undefined>();
    const { formId } = useFormContext();
    const inputingStarted = useRef(false);
    const [disabled, setDisabled] = useState(false);
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);

    const handleCountdownEnd = () => {
        setState('idle');
        setDisabled(true);
    };

    const handleChange = () => {
        if (!inputingStarted.current) {
            inputingStarted.current = true;
            sendEvent({ event: 'site_formInputCode_start', form_id: formId });
        }
        onResetError();
    };
    const handleResend = () => {
        if (codeInputRef.current) {
            codeInputRef.current.reset();
        }
        setCaptchaToken(null);
        setState('counting');
        setDisabled(false);
        onResetError();
        onRetry(captchaToken);
        sendEvent({ event: 'site_formInputCodeButton_click', form_id: formId });
    };

    useEffect(() => {
        if (state === 'idle') {
            sendEvent({ event: 'site_formInputCode_requestCode_show', form_id: formId });
        }
    }, [state, formId]);

    return (
        <div>
            <CodeInput
                autoFocus
                className={cn(styles.codeInput, styles.external, {
                    [styles.hide]: isPending,
                })}
                disabled={disabled}
                error={error}
                fields={CODE_REQUIRED_LENGTH}
                name={name}
                onChange={handleChange}
                onComplete={onComplete}
                onRef={ref => {
                    if (ref && !codeInputRef.current) {
                        codeInputRef.current = ref;
                    }
                }}
                styler={{ marginBottom: 24 }}
            />
            <Spinner
                className={cn(styles.external, {
                    [styles.hide]: !isPending,
                })}
                size="5xl"
                styler={{ marginTop: 8, marginBottom: 12 }}
                theme="red"
                thickness={4}
            />

            {!isPending &&
                (state === 'counting' ? (
                    countdownTime && (
                        <Text>
                            Запросить повторно можно через{' '}
                            <CountdownTimer
                                onStop={handleCountdownEnd}
                                time={secondsToTime(String(countdownTime), 'mm:ss')}
                            />
                        </Text>
                    )
                ) : (
                    <>
                        <Captcha
                            onSuccess={setCaptchaToken}
                            onTokenExpired={() => setCaptchaToken(null)}
                        />
                        <div style={{ height: 24 }} />
                        <Button
                            disabled={!captchaToken}
                            onClick={handleResend}
                            size="m"
                            view="secondary"
                        >
                            Запросить код повторно
                        </Button>
                    </>
                ))}
        </div>
    );
};
